class JackrioTheme {
  constructor() {
    this.header = document.querySelector('[data-site-header]');
    this.drawer = document.querySelector('[data-mobile-drawer]');
    this.search = document.querySelector('[data-search-modal]');
    this.cartDrawer = document.querySelector('[data-cart-drawer]');
    this.activeTrigger = null;
    this.revealObserver = null;
    this.headerScrollHandler = null;
    this.bindGlobalEvents();
    this.initHeader();
    this.initResponsiveState();
    this.initProductForms(document);
    this.initProductGalleries(document);
    this.initProductStickyBars(document);
    this.initRecommendations(document);
    this.initPredictiveSearch(document);
    this.initReveals(document);
  }

  bindGlobalEvents() {
    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const menuButton = target.closest('[data-menu-toggle]');
      const searchButton = target.closest('[data-search-toggle]');
      const cartButton = target.closest('[data-cart-toggle]');
      const cartClose = target.closest('[data-cart-close]');
      const cartQuantity = target.closest('[data-cart-quantity]');
      const quantityButton = target.closest('[data-quantity-button]');
      const facetsButton = target.closest('[data-facets-toggle]');
      const submenuButton = target.closest('[data-submenu-toggle]');
      const mediaThumbnail = target.closest('[data-product-thumbnail]');
      const zoomButton = target.closest('[data-product-zoom]');
      const lightboxClose = target.closest('[data-product-lightbox-close]');
      if (menuButton) { this.activeTrigger = menuButton; this.toggleMenu(); }
      if (searchButton) { this.activeTrigger = searchButton; this.toggleSearch(); }
      if (cartButton) { event.preventDefault(); this.activeTrigger = cartButton; this.openCart(); }
      if (cartClose) this.closeCart();
      if (cartQuantity) this.updateCartLine(cartQuantity);
      if (facetsButton) this.toggleFacets(facetsButton);
      if (quantityButton) this.changeQuantity(quantityButton);
      if (submenuButton) this.toggleSubmenu(submenuButton);
      if (mediaThumbnail) this.activateProductMedia(mediaThumbnail);
      if (zoomButton) this.openProductLightbox(zoomButton);
      if (lightboxClose) this.closeProductLightbox();
      if (target.matches('[data-product-lightbox]')) this.closeProductLightbox();
      if (target.matches('[data-search-modal]')) this.toggleSearch(false);
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeOverlays();
      if (event.key === 'Tab') this.trapFocus(event);
    });
    document.addEventListener('shopify:section:load', (event) => {
      this.header = document.querySelector('[data-site-header]');
      this.drawer = document.querySelector('[data-mobile-drawer]');
      this.search = document.querySelector('[data-search-modal]');
      this.cartDrawer = document.querySelector('[data-cart-drawer]');
      this.initHeader();
      this.initProductForms(event.target);
      this.initProductGalleries(event.target);
      this.initProductStickyBars(event.target);
      this.initRecommendations(event.target);
      this.initPredictiveSearch(event.target);
      this.initReveals(event.target);
    });
  }

  initHeader() {
    if (this.headerScrollHandler) window.removeEventListener('scroll', this.headerScrollHandler);
    this.headerScrollHandler = null;
    if (!this.header?.classList.contains('header--transparent')) return;
    let framePending = false;
    const update = () => {
      this.header?.classList.toggle('is-scrolled', window.scrollY > 30);
      framePending = false;
    };
    this.headerScrollHandler = () => {
      if (framePending) return;
      framePending = true;
      window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', this.headerScrollHandler, { passive: true });
  }

  initResponsiveState() {
    const mobileFacets = window.matchMedia('(max-width: 989px)');
    const update = () => {
      const facets = document.querySelector('[data-facets]');
      if (!facets || facets.classList.contains('is-open')) return;
      facets.setAttribute('aria-hidden', String(mobileFacets.matches));
    };
    update();
    mobileFacets.addEventListener?.('change', update);
  }

  toggleMenu(force, restoreFocus = true) {
    if (!this.drawer) return;
    const open = typeof force === 'boolean' ? force : !this.drawer.classList.contains('is-open');
    if (open) { this.toggleSearch(false, false); this.closeCart(false); }
    this.drawer.classList.toggle('is-open', open);
    this.drawer.setAttribute('aria-hidden', String(!open));
    this.header?.classList.toggle('menu-active', open);
    document.querySelectorAll('[data-menu-toggle]').forEach((button) => button.setAttribute('aria-expanded', String(open)));
    this.syncBodyLock();
    if (open) window.setTimeout(() => this.drawer.querySelector('a, button')?.focus(), 80);
    else if (restoreFocus) this.restoreFocus();
  }

  toggleSearch(force, restoreFocus = true) {
    if (!this.search) return;
    const open = typeof force === 'boolean' ? force : !this.search.classList.contains('is-open');
    if (open) { this.toggleMenu(false, false); this.closeCart(false); }
    this.search.classList.toggle('is-open', open);
    this.search.setAttribute('aria-hidden', String(!open));
    document.querySelectorAll('[data-search-toggle]').forEach((button) => button.setAttribute('aria-expanded', String(open)));
    this.syncBodyLock();
    if (open) window.setTimeout(() => this.search.querySelector('input')?.focus(), 150);
    else if (restoreFocus) this.restoreFocus();
  }

  openCart() {
    if (!this.cartDrawer) return;
    this.toggleMenu(false, false);
    this.toggleSearch(false, false);
    this.cartDrawer.classList.add('is-open');
    this.cartDrawer.setAttribute('aria-hidden', 'false');
    document.querySelectorAll('[data-cart-toggle]').forEach((button) => button.setAttribute('aria-expanded', 'true'));
    this.syncBodyLock();
    window.setTimeout(() => this.cartDrawer.querySelector('[data-cart-close]')?.focus(), 80);
  }

  closeCart(restoreFocus = true) {
    if (!this.cartDrawer) return;
    this.cartDrawer.classList.remove('is-open');
    this.cartDrawer.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-cart-toggle]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    this.syncBodyLock();
    if (restoreFocus) this.restoreFocus();
  }

  toggleFacets(trigger) {
    const facets = document.querySelector('[data-facets]');
    if (!facets) return;
    const open = !facets.classList.contains('is-open');
    facets.classList.toggle('is-open', open);
    facets.setAttribute('aria-hidden', String(!open));
    document.querySelectorAll('[data-facets-toggle]').forEach((button) => button.setAttribute('aria-expanded', String(open)));
    if (open) this.activeTrigger = trigger;
    this.syncBodyLock();
    if (open) window.setTimeout(() => facets.querySelector('button, input, select, a')?.focus(), 80);
    else this.restoreFocus();
  }

  toggleSubmenu(button) {
    const submenu = document.getElementById(button.getAttribute('aria-controls'));
    if (!submenu) return;
    const open = button.getAttribute('aria-expanded') !== 'true';
    button.setAttribute('aria-expanded', String(open));
    button.textContent = open ? '−' : '+';
    submenu.hidden = !open;
  }

  closeOverlays() {
    this.toggleMenu(false, false);
    this.toggleSearch(false, false);
    this.closeCart(false);
    const facets = document.querySelector('[data-facets]');
    facets?.classList.remove('is-open');
    facets?.setAttribute('aria-hidden', 'true');
    document.querySelectorAll('[data-facets-toggle]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    this.syncBodyLock();
    this.restoreFocus();
  }

  syncBodyLock() {
    const overlayOpen = this.drawer?.classList.contains('is-open') || this.search?.classList.contains('is-open') || this.cartDrawer?.classList.contains('is-open') || document.querySelector('[data-facets]')?.classList.contains('is-open');
    document.body.classList.toggle('menu-open', Boolean(overlayOpen));
  }

  restoreFocus() {
    const trigger = this.activeTrigger;
    this.activeTrigger = null;
    if (trigger instanceof HTMLElement && document.contains(trigger)) trigger.focus();
  }

  trapFocus(event) {
    const container = [this.cartDrawer, this.search, this.drawer, document.querySelector('[data-facets]')].find((element) => element && element.classList.contains('is-open'));
    if (!container) return;
    const focusable = [...container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  changeQuantity(button) {
    const input = button.parentElement?.querySelector('input[type="number"]');
    if (!input) return;
    const change = button.dataset.quantityButton === 'plus' ? 1 : -1;
    input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + change);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  initProductGalleries(scope) {
    scope.querySelectorAll('[data-product-lightbox]').forEach((dialog) => {
      if (dialog.dataset.initialized) return;
      dialog.dataset.initialized = 'true';
      dialog.addEventListener('close', () => {
        const image = dialog.querySelector('[data-product-lightbox-image]');
        image?.removeAttribute('src');
        this.restoreFocus();
      });
    });
  }

  activateProductMedia(thumbnail) {
    const gallery = thumbnail.closest('[data-product-gallery]');
    const targetId = thumbnail.dataset.targetMedia;
    if (!gallery || !targetId) return;
    gallery.querySelectorAll('[data-media-id]').forEach((media) => {
      const active = media.dataset.mediaId === targetId;
      media.toggleAttribute('hidden', !active);
      media.classList.toggle('is-active', active);
      if (!active) media.querySelector('video')?.pause();
    });
    gallery.querySelectorAll('[data-product-thumbnail]').forEach((button) => {
      const active = button === thumbnail;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', String(active));
    });
    const thumbnails = [...gallery.querySelectorAll('[data-product-thumbnail]')];
    const mediaCount = gallery.querySelector('[data-product-media-count]');
    const activeIndex = thumbnails.indexOf(thumbnail) + 1;
    if (mediaCount && activeIndex > 0) mediaCount.textContent = `${String(activeIndex).padStart(2, '0')} / ${String(thumbnails.length).padStart(2, '0')}`;
  }

  openProductLightbox(button) {
    const dialog = document.querySelector('[data-product-lightbox]');
    const image = dialog?.querySelector('[data-product-lightbox-image]');
    if (!dialog || !image || !button.dataset.zoomSrc) return;
    this.activeTrigger = button;
    image.src = button.dataset.zoomSrc;
    image.alt = button.dataset.zoomAlt || '';
    if (!dialog.open && typeof dialog.showModal === 'function') dialog.showModal();
  }

  closeProductLightbox() {
    const dialog = document.querySelector('[data-product-lightbox]');
    if (dialog?.open) dialog.close();
  }

  initProductStickyBars(scope) {
    scope.querySelectorAll('[data-product-sticky]').forEach((bar) => {
      if (bar.dataset.initialized) return;
      bar.dataset.initialized = 'true';
      const section = bar.closest('[data-product-section]');
      const form = section?.querySelector('[data-product-form]');
      const mainButton = form?.querySelector('[name="add"]');
      const stickyButton = bar.querySelector('.product-sticky-atc__button');
      const mainPrice = section?.querySelector('[data-product-price]');
      const stickyPrice = bar.querySelector('[data-sticky-price]');
      if (!mainButton || !stickyButton) return;
      const sync = () => {
        stickyButton.disabled = mainButton.disabled;
        stickyButton.textContent = mainButton.textContent;
        if (mainPrice && stickyPrice) stickyPrice.innerHTML = mainPrice.innerHTML;
      };
      sync();
      new MutationObserver(sync).observe(mainButton, { attributes: true, childList: true, subtree: true });
      if (mainPrice) new MutationObserver(sync).observe(mainPrice, { childList: true, subtree: true });
      if (!('IntersectionObserver' in window)) return;
      new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
      }, { threshold: 0 }).observe(mainButton);
    });
  }

  initProductForms(scope) {
    scope.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.initialized) return;
      form.dataset.initialized = 'true';
      let variants = [];
      try { variants = JSON.parse(form.querySelector('[data-variants-json]')?.textContent || '[]'); }
      catch (error) { this.reportError('Product variant data could not be read.', error); }
      const updateOptionAvailability = () => {
        const groups = [...form.querySelectorAll('[data-option-index]')];
        const selected = groups.map((group) => group.querySelector('[data-option-value]:checked')?.value);
        groups.forEach((group, optionIndex) => {
          group.querySelectorAll('[data-option-value]').forEach((input) => {
            input.disabled = !variants.some((variant) => variant.available && variant.options.every((option, index) => index === optionIndex ? option === input.value : (!selected[index] || option === selected[index])));
          });
        });
      };
      updateOptionAvailability();
      form.addEventListener('change', (event) => {
        if (!(event.target instanceof Element) || !event.target.matches('[data-option-value]')) return;
        const selected = [...form.querySelectorAll('[data-option-index]')].map((group) => group.querySelector('[data-option-value]:checked')?.value);
        const variant = variants.find((item) => item.options.every((option, index) => option === selected[index]));
        if (!variant) return;
        updateOptionAvailability();
        const idInput = form.querySelector('[name="id"]');
        if (idInput) idInput.value = variant.id;
        const submit = form.querySelector('[name="add"]');
        if (submit) { submit.disabled = !variant.available; submit.textContent = variant.available ? submit.dataset.addLabel : submit.dataset.soldLabel; }
        const section = form.closest('[data-product-section]');
        const price = section?.querySelector('[data-product-price]');
        if (price) price.innerHTML = variant.compare_at_price > variant.price
          ? `<span class="price__sale">${this.formatMoney(variant.price)}</span><s class="price__compare">${this.formatMoney(variant.compare_at_price)}</s>`
          : `<span>${this.formatMoney(variant.price)}</span>`;
        const salePercent = variant.compare_at_price > variant.price ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) : 0;
        section?.querySelectorAll('[data-product-sale]').forEach((badge) => {
          badge.hidden = salePercent <= 0;
          badge.textContent = `${badge.dataset.productSale === 'long' ? 'You save' : 'Save'} ${salePercent}%`;
        });
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        if (variant.featured_media?.id) {
          const thumbnail = section?.querySelector(`[data-product-thumbnail][data-target-media="${variant.featured_media.id}"]`);
          const media = section?.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (thumbnail) this.activateProductMedia(thumbnail);
          else media?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
        }
      });
      form.addEventListener('submit', (event) => this.addToCart(event, form));
    });
  }

  async addToCart(event, form) {
    const submitter = event.submitter;
    if (submitter && submitter.name !== 'add') return;
    event.preventDefault();
    if (form.dataset.submitting === 'true') return;
    const button = form.querySelector('[name="add"]');
    if (!button || button.disabled) return;
    form.dataset.submitting = 'true';
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = button.dataset.loadingLabel;
    this.clearFormError(form);
    try {
      const response = await fetch(window.Jackrio.routes.cartAdd, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: new FormData(form) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this item.');
      await this.refreshCartDrawer();
      this.activeTrigger = button;
      this.openCart();
    } catch (error) { this.showFormError(form, error.message); }
    finally {
      form.dataset.submitting = 'false';
      button.removeAttribute('aria-busy');
      button.disabled = false;
      button.textContent = button.dataset.addLabel;
    }
  }

  async updateCartLine(button) {
    if (button.disabled) return;
    button.disabled = true;
    try {
      const response = await fetch(window.Jackrio.routes.cartChange, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line: Number(button.dataset.line), quantity: Number(button.dataset.quantity) }) });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update the bag.');
      await this.refreshCartDrawer(cart.item_count);
      this.openCart();
    } catch (error) { this.reportError('The bag could not be updated.', error); button.disabled = false; }
  }

  async refreshCartDrawer(knownCount) {
    const response = await fetch(`${window.Jackrio.routes.cart}?section_id=cart-drawer`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    if (!response.ok) throw new Error('Unable to refresh the bag.');
    const html = await response.text();
    const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-drawer]');
    if (!replacement || !this.cartDrawer) throw new Error('Bag response was incomplete.');
    this.cartDrawer.replaceWith(replacement);
    this.cartDrawer = replacement;
    const count = knownCount ?? Number(replacement.dataset.cartItemCount || 0);
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(count);
      element.closest('.cart-count')?.toggleAttribute('hidden', count === 0);
    });
  }

  clearFormError(form) { const error = form.querySelector('[data-product-error]'); if (error) error.textContent = ''; }
  showFormError(form, message) { const error = form.querySelector('[data-product-error]'); if (error) error.textContent = message; }

  formatMoney(cents) {
    const format = window.themeMoneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount(?:_no_decimals)?\s*\}\}/, value).replace('.00', '');
  }

  initPredictiveSearch(scope) {
    scope.querySelectorAll('[data-predictive-search]').forEach((container) => {
      if (container.dataset.initialized) return;
      container.dataset.initialized = 'true';
      const input = container.querySelector('[data-predictive-input]');
      const results = container.querySelector('[data-predictive-results]');
      if (!input || !results) return;
      let timer;
      let controller;
      input.addEventListener('input', () => {
        window.clearTimeout(timer);
        controller?.abort();
        const query = input.value.trim();
        if (query.length < 2) { results.replaceChildren(); return; }
        timer = window.setTimeout(async () => {
          controller = new AbortController();
          results.setAttribute('aria-busy', 'true');
          try {
            const base = container.dataset.predictiveUrl || window.Jackrio.routes.predictiveSearch;
            const response = await fetch(`${base}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=4`, { signal: controller.signal });
            if (!response.ok) throw new Error('Search suggestions are unavailable.');
            const data = await response.json();
            this.renderPredictiveResults(results, data.resources?.results || {});
          } catch (error) { if (error.name !== 'AbortError') this.reportError('Predictive search failed.', error); }
          finally { results.removeAttribute('aria-busy'); }
        }, 220);
      });
    });
  }

  renderPredictiveResults(container, groups) {
    const products = groups.products || [];
    const collections = groups.collections || [];
    const pages = groups.pages || [];
    if (!products.length && !collections.length && !pages.length) { container.textContent = ''; return; }
    const fragment = document.createDocumentFragment();
    if (products.length) fragment.append(this.createSearchGroup(products, container.closest('[data-predictive-search]')?.dataset.productsLabel || 'Products', true));
    if (collections.length) fragment.append(this.createSearchGroup(collections, container.closest('[data-predictive-search]')?.dataset.collectionsLabel || 'Collections'));
    if (pages.length) fragment.append(this.createSearchGroup(pages, container.closest('[data-predictive-search]')?.dataset.pagesLabel || 'Pages'));
    container.replaceChildren(fragment);
  }

  createSearchGroup(items, label, showImage = false) {
    const section = document.createElement('section');
    section.className = 'predictive-search__group';
    const heading = document.createElement('h2');
    heading.className = 'predictive-search__heading';
    heading.textContent = label;
    const list = document.createElement('ul');
    items.forEach((item) => {
      const row = document.createElement('li');
      const link = document.createElement('a');
      link.href = item.url;
      link.className = 'predictive-search__link';
      const imageUrl = item.featured_image?.url || item.image?.url;
      if (showImage && imageUrl) {
        const image = document.createElement('img');
        image.src = imageUrl;
        image.alt = item.featured_image?.alt || item.title || '';
        image.width = 64;
        image.height = 80;
        image.loading = 'lazy';
        link.append(image);
      }
      const title = document.createElement('span');
      title.textContent = item.title;
      link.append(title);
      row.append(link);
      list.append(row);
    });
    section.append(heading, list);
    return section;
  }

  initRecommendations(scope) {
    scope.querySelectorAll('[data-recommendations-url]').forEach((element) => {
      if (element.dataset.initialized) return;
      element.dataset.initialized = 'true';
      fetch(element.dataset.recommendationsUrl)
        .then((response) => { if (!response.ok) throw new Error('Recommendations request failed.'); return response.text(); })
        .then((text) => {
          const replacement = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-product-recommendations]');
          if (replacement?.innerHTML.trim()) element.innerHTML = replacement.innerHTML;
        })
        .catch((error) => this.reportError('Product recommendations could not be loaded.', error));
    });
  }

  initReveals(scope) {
    const motionGroups = [
      { selector: '.section-heading, .editorial-split__content-inner, .editorial-banner__content, .collection-hero__content, .newsletter-section__inner, .quote-section blockquote, .quote-section cite, .page-hero > *', type: 'copy', step: 55 },
      { selector: '.editorial-split__media, .editorial-banner__media, .collection-hero__media', type: 'media', step: 0 },
      { selector: '.product-card, .category-card, .usp, .search-result, .product-benefit', type: 'card', step: 70 },
      { selector: '.site-footer .footer__grid > *, .site-footer .footer__bottom', type: 'copy', step: 65 }
    ];
    const candidates = [];
    motionGroups.forEach((group) => {
      const matches = [
        ...(scope instanceof Element && scope.matches(group.selector) ? [scope] : []),
        ...scope.querySelectorAll(group.selector)
      ];
      matches.forEach((element) => {
        if (element.dataset.revealInitialized) return;
        element.dataset.revealInitialized = 'true';
        element.dataset.reveal = group.type;
        const siblings = [...(element.parentElement?.children || [])].filter((sibling) => sibling.matches?.(group.selector));
        const siblingIndex = Math.max(0, siblings.indexOf(element));
        element.style.setProperty('--reveal-delay', `${Math.min(siblingIndex * group.step, 280)}ms`);
        candidates.push(element);
      });
    });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || window.Jackrio?.designMode || !('IntersectionObserver' in window)) {
      candidates.forEach((element) => element.classList.add('is-revealed'));
      return;
    }
    if (!this.revealObserver) {
      this.revealObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => { if (entry.isIntersecting) { entry.target.classList.add('is-revealed'); observer.unobserve(entry.target); } });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    }
    candidates.forEach((element) => {
      if (element.getBoundingClientRect().top < window.innerHeight * 0.92) {
        window.requestAnimationFrame(() => element.classList.add('is-revealed'));
        return;
      }
      this.revealObserver.observe(element);
    });
  }

  reportError(message, error) { if (window.Jackrio?.designMode) console.error(message, error); }
}

document.addEventListener('DOMContentLoaded', () => {
  window.themeMoneyFormat = window.themeMoneyFormat || '${{amount}}';
  window.jackrioTheme = new JackrioTheme();
});
